import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Image, Form } from "react-bootstrap";

import "bootstrap/dist/css/bootstrap.min.css";
import "./entercode.css";

import responsivebg from "../../../assets/images/responsive-bg.png";
import backarrow from "../../../assets/images/backarrow.jpg";
import logo2 from "../../../assets/images/logo2.png";

import Stepper from "../Stepper";
import { useCookies } from "../../../hooks/useCookies";
import { formatPhoneNumber } from "../../../utils/utils";
import * as verificationAction from "../../../actions/verification"

export const Entercode = () => {
  const { getCookie, setCookie } = useCookies();
  const [phoneNumber, setPhoneNumber] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const goToEmailVerify = () => {
    navigate("/emailverify");
  };

  // State for OTP code inputs
  const [code1, setCode1] = useState("");
  const [code2, setCode2] = useState("");
  const [code3, setCode3] = useState("");
  const [code4, setCode4] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Refs for input fields
  const code1ref = useRef(null);
  const code2ref = useRef(null);
  const code3ref = useRef(null);
  const code4ref = useRef(null);

  // Resend OTP timer state and handler
  const [resendTimer, setResendTimer] = useState(120);
  const [isResendDisabled, setIsResendDisabled] = useState(true); // Initially disabled

  useEffect(() => {
    // Get phone number from cookies
    const savedPhoneNumber = getCookie("phoneNumber");
    if (savedPhoneNumber) {
      setPhoneNumber(savedPhoneNumber);
    }

    // Countdown logic
    const interval = setInterval(() => {
      setResendTimer((prevTimer) => {
        if (prevTimer > 0) {
          return prevTimer - 1;
        } else {
          setIsResendDisabled(false); // Enable resend button when timer reaches 0
          clearInterval(interval);
          return 0;
        }
      });
    }, 1000);

    // Clean up interval on component unmount
    return () => clearInterval(interval);
  }, [getCookie, location.state]);

  // Function to handle OTP code input change
  const handleCodeChange = (e, setter, nextRef) => {
    const value = e.target.value;
    if (!isNaN(value) && value?.length <= 1) {
      setter(value);

      if (value?.length === 1 && nextRef) {
        nextRef.current.focus();
      }
    }
  };

  // Function to handle Backspace and focus on previous input
  const handleKeyDown = (e, prevRef) => {
    if (e.key === "Backspace" && e.target.value === "" && prevRef) {
      prevRef.current.focus();
    }
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    const otp = code1 + code2 + code3 + code4;
    if (otp?.length !== 4) {
      setErrorMessage("*Code is required.");
    } else if (otp?.length !== 4) {
      setErrorMessage("Invalid verification code.");
    } else {
      setErrorMessage("");
      try {
        let body = JSON.stringify({
          phone: phoneNumber,
          otp: otp,
        })
        const response = await verificationAction.signupCode(body)
        if (response?.userId) {
          setResendTimer(120);
          setErrorMessage("");
          setIsResendDisabled(true);
          setCookie("token", response.token, 30);
          setCookie("userId", response.userId, 30);
          goToEmailVerify();
        } else {
          setErrorMessage(response?.response?.data.message || response?.message || "*Failed to send code");
        }
      } catch (error) {
        console.error("Error:", error);
        setErrorMessage("An error occurred. Please try again later.");
      }
    }
  };

  // Function to resend OTP
  const resendotp = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    if (resendTimer === 0) {
      try {
        let body = JSON.stringify({
          phone: phoneNumber,
        })
        const response = await verificationAction.signupOtp(body);
        if (response) {
          setResendTimer(120); // Reset timer to 2 minutes on successful resend
          setIsResendDisabled(true); // Disable resend button after OTP is sent
        } else {
          setErrorMessage("*Failed to send OTP.");
        }
      } catch (error) {
        console.error("Error:", error);
        setErrorMessage("An error occurred. Please try again later.");
      }
    }
  };

  // Function to format remaining time in mm:ss format
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  return (
    <div className="entercode-container">
      <div className="entercode-bg">
        <Image className="responsive-bg" src={responsivebg}></Image>
      </div>

      <Container className="entercode-main">
        <Container className="entercode-box">
          <Container className="logo-progressbar1">
            <Container className="logo-arrow1">
              <Image
                src={backarrow}
                className="backarrow"
                onClick={() => window.history.back()}
              />
              <Image
                src={logo2}
                alt="Logo"
                className="logo1"
                style={{ backgroundColor: "transparent" }}
              />
            </Container>
            <Stepper activeStep="Phone" />
          </Container>

          <Form onSubmit={handleSubmit} className="entercode-form">
            <Container className="entercode-content">
              <Form.Group controlId="formCode" className="entercode-form-group">
                <Form.Label className="entercode-lable">Enter your verification code</Form.Label>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Form.Control
                    className={`entercode-input ${errorMessage ? "error" : ""
                      }`}
                    type="tel"
                    ref={code1ref}
                    placeholder=""
                    value={code1}
                    onChange={(e) =>
                      handleCodeChange(e, setCode1, code2ref)
                    }
                    onKeyDown={(e) => handleKeyDown(e, null)}
                    style={{ flex: 1 }}
                  />
                  <Form.Control
                    className={`entercode-input ${errorMessage ? "error" : ""
                      }`}
                    type="tel"
                    ref={code2ref}
                    placeholder=""
                    value={code2}
                    onChange={(e) =>
                      handleCodeChange(e, setCode2, code3ref)
                    }
                    onKeyDown={(e) => handleKeyDown(e, code1ref)}
                    style={{ flex: 1, marginLeft: "10px" }}
                  />
                  <Form.Control
                    className={`entercode-input ${errorMessage ? "error" : ""
                      }`}
                    type="tel"
                    ref={code3ref}
                    placeholder=""
                    value={code3}
                    onChange={(e) =>
                      handleCodeChange(e, setCode3, code4ref)
                    }
                    onKeyDown={(e) => handleKeyDown(e, code2ref)}
                    style={{ flex: 1, marginLeft: "10px" }}
                  />
                  <Form.Control
                    className={`entercode-input ${errorMessage ? "error" : ""
                      }`}
                    type="tel"
                    ref={code4ref}
                    placeholder=""
                    value={code4}
                    onChange={(e) => handleCodeChange(e, setCode4, null)}
                    onKeyDown={(e) => handleKeyDown(e, code3ref)}
                    style={{ flex: 1, marginLeft: "10px" }}
                  />
                </div>
                {phoneNumber && (
                  <p style={{ marginBottom: "5px", color: "#666", fontSize: "14px" }}>
                    Code sent to {formatPhoneNumber(phoneNumber)}
                  </p>
                )}
                <div style={{ marginTop: "5px" }}>
                  {errorMessage && (
                    <Form.Text className="text-danger error-message">
                      {errorMessage}
                    </Form.Text>
                  )}
                </div>
                <div className="resend-timer">
                  <button
                    type="button"
                    onClick={resendotp}
                    disabled={isResendDisabled}
                    style={{
                      color: isResendDisabled ? "#cbcbcb" : "#5E5E5E",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: isResendDisabled ? "default" : "pointer",
                    }}
                  >
                    Resend code
                  </button>
                  <span>{formatTime(resendTimer)} sec</span>
                </div>
              </Form.Group>
              <button type="submit" className="global-next-btn">
                Next
              </button>

            </Container>

          </Form>
        </Container>
      </Container>
    </div>
  );
};
